import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchSyncJobRuns } from "../features/syncJobs/syncJobsApi";
import {
  selectSyncJobRuns,
  selectSyncJobsLoading,
} from "../features/syncJobs/syncJobsSlice";
import { SyncJobRun } from "../features/syncJobs/SyncJobRun";

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

const formatDateTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString();
};

const getStatusColor = (
  status: string
): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "Completed":
    case "Success":
      return "success";
    case "Failed":
      return "error";
    case "PartiallyFailed":
    case "Interrupted":
      return "warning";
    case "Running":
      return "info";
    case "Canceled":
      return "default";
    default:
      return "default";
  }
};

const getElapsedMs = (startedAt: string): number => {
  return Date.now() - new Date(startedAt).getTime();
};

interface SubJob {
  name: string;
  status: string | null;
  error: string | null;
}

const getSubJobs = (run: SyncJobRun): SubJob[] => [
  {
    name: "Clockify Sync",
    status: run.clockifySyncStatus,
    error: run.clockifySyncError,
  },
  {
    name: "Metrics Import",
    status: run.metricsImportStatus,
    error: run.metricsImportError,
  },
  {
    name: "Task Report Import",
    status: run.taskReportImportStatus,
    error: run.taskReportImportError,
  },
  {
    name: "Clockify Time Entry Import",
    status: run.clockifyTimeEntryImportStatus,
    error: run.clockifyTimeEntryImportError,
  },
  {
    name: "Alert Emails",
    status: run.alertEmailsStatus,
    error: run.alertEmailsError,
  },
];

const SyncJobRow = ({ run }: { run: SyncJobRun }) => {
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const subJobs = getSubJobs(run);
  const hasSubJobData = subJobs.some((sj) => sj.status !== null);
  const isRunning = run.status === "Running";

  useEffect(() => {
    if (!isRunning) return;
    setElapsed(getElapsedMs(run.startedAt));
    const interval = setInterval(() => {
      setElapsed(getElapsedMs(run.startedAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, run.startedAt]);

  const displayDuration = isRunning
    ? elapsed
    : run.durationMs > 0
      ? run.durationMs
      : 0;

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { backgroundColor: "rgba(14, 134, 212, 0.03)" },
          cursor: hasSubJobData ? "pointer" : "default",
        }}
        onClick={() => hasSubJobData && setOpen(!open)}
      >
        <TableCell sx={{ width: 40, px: 1 }}>
          {hasSubJobData && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{formatDateTime(run.startedAt)}</TableCell>
        <TableCell>
          {displayDuration > 0 ? formatDuration(displayDuration) : "—"}
        </TableCell>
        <TableCell>
          <Chip
            label={run.status}
            color={getStatusColor(run.status)}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, minWidth: 100 }}
          />
        </TableCell>
        <TableCell align="right">{run.projectsUpdated}</TableCell>
        <TableCell align="right">{run.milestonesUpdated}</TableCell>
        <TableCell align="right">
          {run.totalHoursImported > 0
            ? run.totalHoursImported.toFixed(1)
            : "—"}
        </TableCell>
        <TableCell>
          {run.errorMessage && (
            <Tooltip title={run.errorMessage}>
              <Typography
                variant="caption"
                color="error"
                sx={{
                  maxWidth: 200,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {run.errorMessage}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {hasSubJobData && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 600, color: "text.secondary" }}
                >
                  Sub-job Details
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Sub-job</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subJobs
                      .filter((sj) => sj.status !== null)
                      .map((sj) => (
                        <TableRow key={sj.name}>
                          <TableCell>{sj.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={sj.status}
                              color={getStatusColor(sj.status || "")}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            {sj.error && (
                              <Typography variant="caption" color="error">
                                {sj.error}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SyncDashboardPage = () => {
  const dispatch = useAppDispatch();
  const syncJobRuns = useAppSelector(selectSyncJobRuns);
  const isLoading = useAppSelector(selectSyncJobsLoading);
  const hasRunningJob = syncJobRuns.some((r) => r.status === "Running");

  const refreshData = useCallback(() => {
    dispatch(fetchSyncJobRuns());
  }, [dispatch]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto-refresh every 10s when a job is running
  useEffect(() => {
    if (!hasRunningJob) return;
    const interval = setInterval(refreshData, 10000);
    return () => clearInterval(interval);
  }, [hasRunningJob, refreshData]);

  return (
    <Container maxWidth="lg" sx={{ px: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ textAlign: "left", mt: 4, mb: 3 }}
      >
        Sync Job History
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : syncJobRuns.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No sync runs yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sync job history will appear here after the first import run.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Projects
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Milestones
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Hours
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncJobRuns.map((run) => (
                <SyncJobRow key={run.id} run={run} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default SyncDashboardPage;
